import {
  ComponentPropsWithoutRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import BaseLinkOrButton from "./BaseLinkOrButton.tsx";
import styles from "./BigSteps.module.scss";

interface BigStepsProps {
  title: ReactNode;
  steps: { title: string; detail: ReactNode }[];
  buttonProps?: ComponentPropsWithoutRef<typeof BaseLinkOrButton>;
}

interface Point {
  x: number;
  y: number;
}

function DottedLine({ from, to }: { from: Point; to: Point }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={true}
      focusable={false}
      width="100%"
      height="100%"
      className={styles.dottedLine}
      style={{ position: "absolute", top: 0, left: 0 }}
    >
      <line x1={from.x} x2={to.x} y1={from.y} y2={to.y} />
    </svg>
  );
}
export default function BigSteps({ title, steps, buttonProps }: BigStepsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstNumberRef = useRef<HTMLDivElement>(null);
  const lastNumberRef = useRef<HTMLDivElement>(null);
  const [lineCoords, setLineCoords] = useState({
    from: { x: 0, y: 0 },
    to: { x: 0, y: 0 },
  });

  const redrawLine = useCallback(() => {
    if (firstNumberRef.current && lastNumberRef.current) {
      setLineCoords({
        from: {
          x:
            firstNumberRef.current.offsetLeft +
            firstNumberRef.current.offsetWidth / 2,
          y:
            firstNumberRef.current.offsetTop +
            firstNumberRef.current.offsetHeight / 2,
        },
        to: {
          x:
            lastNumberRef.current.offsetLeft +
            lastNumberRef.current.offsetWidth / 2,
          y:
            lastNumberRef.current.offsetTop +
            lastNumberRef.current.offsetHeight / 2,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        redrawLine();
      });

      resizeObserver.observe(containerRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [redrawLine]);

  if (steps.length !== 3) {
    // eslint-disable-next-line no-console
    console.error("BigSteps: CSS currently only supports three steps. Hiding.");
    return null;
  }

  return (
    <div className={styles.outerContainer} ref={containerRef}>
      <div className={styles.innerContainer}>
        <h2>{title}</h2>
        <DottedLine from={lineCoords.from} to={lineCoords.to} />
        <ol>
          {steps.map((step, i) => (
            <li key={step.title}>
              <div
                className={styles.stepNumber}
                ref={
                  i === 0
                    ? firstNumberRef
                    : i === steps.length - 1
                    ? lastNumberRef
                    : undefined
                }
              >
                {i + 1}
              </div>
              <div className={styles.stepText}>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p>{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
        {buttonProps && (
          <BaseLinkOrButton
            {...buttonProps}
            className="vst-button-pill vst-button-variant-outline"
          />
        )}
      </div>
    </div>
  );
}
